'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ComponentType, FC, ReactElement } from 'react';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/app/components/ui/context-menu';
import { useDownload } from '../_hooks/use-download';
import { type FileOperationType, getContextOperations } from '../_utils/file-operations-config';
import type { FileItem } from '../types';

interface FileContextMenuProps {
  item: FileItem;
  isLoading?: FileOperationType | null;
  onOperation: (operation: FileOperationType, item: FileItem) => void;
  onContextMenu?: (item: FileItem) => void;
  children: React.ReactNode;
  isBatch?: boolean;
}

export const FileContextMenu: FC<FileContextMenuProps> = ({
  item,
  isLoading,
  onOperation,
  onContextMenu,
  children,
  isBatch = false,
}) => {
  const pathname = usePathname();
  const { getDownloadMenuItems } = useDownload();
  const t = useTranslations();

  const contextOperations = getContextOperations(isBatch, item.type === 'folder');

  const handleOperationClick = (operationType: FileOperationType) => {
    onOperation(operationType, item);
  };

  const handleContextMenuTrigger = () => {
    onContextMenu?.(item);
  };

  // 渲染下载菜单项（带水印项用锚点以满足 GPT 插页「点击锚点」触发展示）
  const renderDownloadMenuItems = (Icon: ComponentType<{ className?: string }>) => {
    const downloadMenuItems = getDownloadMenuItems(item, Icon, true);

    return downloadMenuItems.map((menuItem) => {
      const content = (
        <>
          <menuItem.icon className="text-base" />
          <div>
            <div>{menuItem.label}</div>
            {menuItem.badge && (
              <span className="ml-1 scale-[0.8] rounded-[100px] bg-system-blue02 px-2 py-0.5 text-white text-xs">
                {menuItem.badge}
              </span>
            )}
            {menuItem.disabled && menuItem.disabledTip && (
              <div className="text-system-white64 text-xs">{menuItem.disabledTip}</div>
            )}
          </div>
        </>
      );
      if (menuItem.renderAsAnchor) {
        return (
          <ContextMenuItem key={menuItem.key} asChild disabled={menuItem.disabled}>
            <a
              href={pathname ?? '/'}
              onClick={(e) => {
                e.preventDefault();
                if (menuItem.disabled) return;
                menuItem.onClick();
              }}
              className="flex cursor-pointer select-none items-center outline-none"
            >
              {content}
            </a>
          </ContextMenuItem>
        );
      }
      return (
        <ContextMenuItem
          key={menuItem.key}
          onClick={() => {
            if (menuItem.disabled) return;
            menuItem.onClick();
          }}
          disabled={menuItem.disabled}
        >
          {content}
        </ContextMenuItem>
      );
    });
  };

  const renderOperations = () => {
    const elements: ReactElement[] = [];

    for (let i = 0; i < contextOperations.length; i++) {
      const operation = contextOperations[i];
      const Icon = operation.icon;
      const isCurrentLoading = isLoading === operation.id;

      if (operation.id === 'download') {
        // 使用专门的函数渲染下载菜单项
        elements.push(...renderDownloadMenuItems(Icon));
      } else {
        // 其他操作菜单项
        elements.push(
          <ContextMenuItem
            key={operation.id}
            onClick={() => handleOperationClick(operation.id)}
            disabled={isCurrentLoading}
          >
            {isCurrentLoading ? (
              <BaseLineLoading2 className="animate-spin text-base" />
            ) : (
              <Icon className="text-base" />
            )}
            {t(`creations.operations.${operation.id}`)}
          </ContextMenuItem>,
        );
      }
    }

    return elements;
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild onContextMenu={handleContextMenuTrigger}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-fit">{renderOperations()}</ContextMenuContent>
    </ContextMenu>
  );
};
